import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", password: "", role: "ejecutivo", active: true, allowed_company_ids: [], primary_company_id: "" };
const ROLE_COLOR = {
  admin: "bg-danger/10 text-danger border-danger/30",
  capturista: "bg-warning/10 text-warning border-warning/30",
  ejecutivo: "bg-info/10 text-info border-info/30",
};

export default function Usuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/users").then((res) => setUsers(res.data));
  useEffect(() => {
    load();
    api.get("/companies").then((res) => setCompanies(res.data));
  }, []);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || id;

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (u) => {
    setForm({
      name: u.name, email: u.email, password: "", role: u.role, active: u.active,
      allowed_company_ids: u.allowed_company_ids || [],
      primary_company_id: u.primary_company_id || "",
    });
    setEditing(u.id);
    setOpen(true);
  };

  const toggleAllowed = (id) => {
    setForm((f) => {
      const on = f.allowed_company_ids.includes(id);
      const allowed = on ? f.allowed_company_ids.filter((x) => x !== id) : [...f.allowed_company_ids, id];
      let primary = f.primary_company_id;
      if (on) {
        // removed a chip: if it was the primary (or primary no longer valid), pick the first remaining
        if (primary === id || !allowed.includes(primary)) primary = allowed[0] || "";
      } else if (!primary) {
        primary = id; // first selected becomes primary
      }
      return { ...f, allowed_company_ids: allowed, primary_company_id: primary };
    });
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = {
          name: form.name, role: form.role, active: form.active,
          allowed_company_ids: form.allowed_company_ids,
          primary_company_id: form.primary_company_id || null,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success("Usuario guardado");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Usuarios y Permisos</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gestión de roles: Administrador, Capturista, Ejecutivo/Visor.</p>
        </div>
        <Button onClick={openNew} data-testid="new-user-button">
          <Plus size={18} className="mr-1" aria-hidden="true" /> Nuevo usuario
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-accent/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Correo</th>
              <th className="px-4 py-3">Rol</th>
              <th className="px-4 py-3">Empresas</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-accent/30" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-2.5 font-medium">{u.name}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={ROLE_COLOR[u.role]}>{ROLE_LABELS[u.role]}</Badge>
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground" data-testid={`user-companies-${u.email}`}>
                  {u.role === "admin" ? (
                    <span>Todas</span>
                  ) : (u.allowed_company_ids?.length ? (
                    <span>
                      {u.primary_company_id && <strong className="text-foreground">{companyName(u.primary_company_id)}</strong>}
                      {u.allowed_company_ids.length > 1 && ` +${u.allowed_company_ids.length - 1}`}
                    </span>
                  ) : <span>Todas</span>)}
                </td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={u.active ? "bg-success/10 text-success border-success/30" : "bg-muted text-muted-foreground"}>
                    {u.active ? "Activo" : "Inactivo"}
                  </Badge>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                      <PencilSimple size={16} />
                    </Button>
                    {u.id !== me?.id && (
                      <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}>
                        <Trash size={16} className="text-danger" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-1.5">
              <Label>Correo</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="capturista">Capturista</SelectItem>
                  <SelectItem value="ejecutivo">Ejecutivo / Visor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <Label>Usuario activo</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" />
              </div>
            )}

            {form.role !== "admin" && (
              <>
                <div className="space-y-1.5">
                  <Label>Empresas permitidas</Label>
                  <div className="flex flex-wrap gap-2" data-testid="user-allowed-companies">
                    {companies.map((c) => {
                      const on = form.allowed_company_ids.includes(c.id);
                      return (
                        <button
                          type="button" key={c.id} onClick={() => toggleAllowed(c.id)}
                          data-testid={`allowed-company-${c.code}`}
                          className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                    {companies.length === 0 && <span className="text-xs text-muted-foreground">No hay empresas.</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">Sin selección = acceso a todas las empresas.</p>
                </div>

                {form.allowed_company_ids.length > 0 && (
                  <div className="space-y-1.5">
                    <Label>Empresa principal (por defecto)</Label>
                    <Select value={form.primary_company_id || ""} onValueChange={(v) => setForm({ ...form, primary_company_id: v })}>
                      <SelectTrigger data-testid="user-primary-company"><SelectValue placeholder="Selecciona la empresa por defecto" /></SelectTrigger>
                      <SelectContent>
                        {form.allowed_company_ids.map((id) => (
                          <SelectItem key={id} value={id}>{companyName(id)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-user-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
